import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, TrendingUp, PieChart as PieIcon, ChevronDown } from 'lucide-react';
import axios from 'axios';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';

// Extended color palettes for vibrant graphs
const vibrantColors = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
  '#BB8FCE', '#85C1E9', '#82E0AA', '#F8C471', '#F1948A', '#85C1E9', '#D7BDE2', '#F9E79F',
  '#A9DFBF', '#F5B7B1', '#D4E6F1', '#FAD7A0', '#ABEBC6', '#E8DAEF', '#FDEBD0', '#D1F2EB',
  '#F5CBA7', '#D6EAF8', '#D5F4E6', '#FCF3CF', '#EBDEF0', '#FADBD8', '#D6EAF8', '#D5F4E6'
];

const gradientColors = [
  '#FF3E3E', '#FF6B6B', '#FF8E8E', '#FFB5B5', '#FFDCDC', // Red gradient
  '#4ECDC4', '#45B7D1', '#54A0FF', '#5DADE2', '#85C1E9', // Blue-teal gradient
  '#FF9F43', '#FECA57', '#FFD166', '#FFEAA7', '#F9E79F', // Yellow-orange gradient
  '#6C5CE7', '#A29BFE', '#D6A2E8', '#FDA7DF', '#FFC8DD', // Purple-pink gradient
  '#00B894', '#55EFC4', '#81ECEC', '#74B9FF', '#0984E3', // Green-blue gradient
  '#E17055', '#FDCB6E', '#FF7675', '#FD79A8', '#A29BFE'  // Rainbow gradient
];

const rainbowColors = [
  '#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#9400D3',
  '#FF1493', '#00CED1', '#32CD32', '#FF4500', '#DA70D6', '#6495ED', '#FF69B4',
  '#00FA9A', '#8A2BE2', '#DC143C', '#00BFFF', '#FF8C00', '#7CFC00', '#ADFF2F'
];



// Function to generate random colors
const generateRandomColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = Math.floor(Math.random() * 360);
    const saturation = 70 + Math.random() * 30;
    const lightness = 50 + Math.random() * 30;
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }
  return colors;
};

// Function to get colors for charts based on data length
const getChartColors = (dataLength, chartType = 'bar') => {
  if (chartType === 'pie' || chartType === 'radar') {
    // For pie/radar charts, use vibrant colors
    return vibrantColors.slice(0, Math.min(dataLength, vibrantColors.length));
  } else if (chartType === 'bar') {
    // For bar charts, use gradient colors
    if (dataLength <= gradientColors.length) {
      return gradientColors.slice(0, dataLength);
    } else {
      return generateRandomColors(dataLength);
    }
  } else if (chartType === 'line') {
    // For line charts, use rainbow colors
    return rainbowColors.slice(0, Math.min(dataLength, 5)); // Max 5 lines for readability
  }
  return vibrantColors.slice(0, Math.min(dataLength, vibrantColors.length));
};

export default function AnalyticsTab({ fromDate = null, toDate = null, departmentguid = null }) {
  const [examTitles, setExamTitles] = useState([]);
  const [titlesError, setTitlesError] = useState(null); // New: for titles error
  const [selectedTitle, setSelectedTitle] = useState('');
  const [currentExamTitle, setCurrentExamTitle] = useState('Loading latest assessment...');
  const [passThreshold, setPassThreshold] = useState(70);
  const [pieData, setPieData] = useState([]);
  const [loadingPie, setLoadingPie] = useState(true);
  const [loadingTitles, setLoadingTitles] = useState(true);

  const [dateRangeData, setDateRangeData] = useState([]);
  const [dateChartType, setDateChartType] = useState('bar');
  const [loadingDate, setLoadingDate] = useState(true);
  const [showDateFilters, setShowDateFilters] = useState(false);

  const [trendData, setTrendData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('yearly');
  const [trendChartType, setTrendChartType] = useState('line');
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [showTrendFilters, setShowTrendFilters] = useState(false);

  const [showExamSelect, setShowExamSelect] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState(null); // For date range
  const [pieMessage, setPieMessage] = useState(''); // For pie
  const [trendMessage, setTrendMessage] = useState(''); // For trend

  // New state to track overall loading status
  const [isLoading, setIsLoading] = useState(true);

  // Get colors for each chart
  const dateBarColors = useMemo(() =>
    getChartColors(dateRangeData.length, 'bar'), [dateRangeData.length]
  );

  const trendBarColors = useMemo(() =>
    getChartColors(trendData.length, 'bar'), [trendData.length]
  );

  const pieChartColors = useMemo(() =>
    getChartColors(pieData.length, 'pie'), [pieData.length]
  );

  const trendPieColors = useMemo(() =>
    getChartColors(trendData.length, 'pie'), [trendData.length]
  );

  // Set default date range to one year back if no dates are provided
  const effectiveFromDate = useMemo(() => {
    if (fromDate) return fromDate;

    // Default: one year ago from today
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return oneYearAgo;
  }, [fromDate]);

  const effectiveToDate = useMemo(() => {
    return toDate || new Date();
  }, [toDate]);

  useEffect(() => {
    if (!departmentguid) return;

    fetchExamTitles();
    fetchPieSummary();
    fetchDateRangeData();
    fetchTrendData();
    setLoadingTitles(true);
    setLoadingPie(true);
    setLoadingDate(true);
    setLoadingTrend(true);
    setIsLoading(true);
    setTitlesError(null); // Reset errors on department change
    setEmptyMessage(null);
    setPieMessage('');
    setTrendMessage('');
    setExamTitles([]); // Reset data
    setDateRangeData([]);
    setTrendData([]);
    setPieData([]);
  }, [departmentguid]);

  useEffect(() => {
    if (
      !loadingDate &&
      !loadingTrend &&
      !loadingPie &&
      !loadingTitles
    ) {
      setIsLoading(false);
    }
  }, [loadingDate, loadingTrend, loadingPie, loadingTitles]);

  const fetchExamTitles = async () => {
    setLoadingTitles(true);
    setTitlesError(null);
    try {
      const res = await axios.get('/managerdashboard/exam-titles/', {
        params: { department_guid: departmentguid }
      });

      if (!res.data?.success) {
        throw new Error(res.data.error || 'Failed to fetch exam titles');
      }



      const titles = res.data.exams
        .filter(t => t?.trim())
        .map(t => t.trim());

      setExamTitles(titles);
      // setSelectedTitle(titles[0] || '');
      // setCurrentExamTitle(titles[0] || 'Unknown Exam');
    } catch (err) {
      console.error('❌ Titles API failed', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load exam titles. Please try again.';
      setTitlesError(errorMsg);
      setExamTitles([]);
      if (err.response?.status === 401) {
        console.warn('Unauthorized - Redirecting to login...');
        // navigate('/login'); // Uncomment if needed
      }
    } finally {
      setLoadingTitles(false);
    }
  };

  // fetchPieSummary
  const fetchPieSummary = async (title = null) => {
    setLoadingPie(true);
    setPieMessage('');

    try {
      const res = await axios.get('/managerdashboard/exam-result/', {
        params: { title, department_guid: departmentguid }
      });

      if (res.data?.success) {
        const data = res.data.chart_data || [];
        setPieData(data);
        setPassThreshold(res.data.pass_threshold || 70);

        const returnedTitle = res.data.exam_title?.trim() || title || 'Result Summary';
        setCurrentExamTitle(returnedTitle);
        setSelectedTitle(returnedTitle);

        const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);
        if (data.length === 0 || totalValue === 0) {
          setPieMessage('No data found for this exam');
        } else {
          setPieMessage('');
        }
      } else {
        throw new Error(res.data.error || 'Failed');
      }
    } catch (err) {
      console.error('Pie API failed', err);
      setPieData([]);
      setPieMessage('No data found for this exam');
      setCurrentExamTitle(title || 'Result Summary');
      setSelectedTitle('');
    } finally {
      setLoadingPie(false);
    }
  };

  const fetchDateRangeData = async () => {
    setLoadingDate(true);
    setEmptyMessage(null);  // Reset
    try {
      const res = await axios.get('/managerdashboard/exam-performance/', {
        params: {
          from_date: effectiveFromDate.toLocaleDateString('en-CA'), // YYYY-MM-DD in local time
          to_date: effectiveToDate.toLocaleDateString('en-CA'),
          department_guid: departmentguid
        }
      });
      if (res.data?.success) {
        const data = res.data.data || [];
        setDateRangeData(
          data.map(d => ({
            name: d.exam && d.exam.length > 18 ? d.exam.substring(0, 18) + '...' : d.exam || 'Unknown',
            avgScore: d.avgScore || 0,
            fullExamName: d.exam || 'Unknown'
          }))
        );
        if (data.length === 0) {
          setEmptyMessage(res.data.message || 'No exam data found for the selected date range.');
        }
      } else {
        throw new Error(res.data.error || 'Failed to fetch performance data');
      }
    } catch (err) {
      console.error('❌ Date API failed', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load performance data. Please try again.';
      setEmptyMessage(errorMsg);
      setDateRangeData([]);
      if (err.response?.status === 401) {
        console.warn('Unauthorized - Redirecting to login...');
        // navigate('/login');
      }
    } finally {
      setLoadingDate(false);
    }
  };

  const fetchTrendData = async () => {
    setLoadingTrend(true);
    setTrendMessage('');

    try {
      const res = await axios.get('/managerdashboard/exams-created/', {
        params: { period: selectedPeriod, department_guid: departmentguid }
      });

      if (res.data?.success) {
        const data = res.data.data || [];
        const formattedData = data.map(d => ({ name: d.period, examCount: d.examCount }));
        setTrendData(formattedData);

        const hasData = formattedData.some(d => d.examCount > 0);
        if (!hasData) {
          setTrendMessage('No data available');
        }
      } else {
        throw new Error(res.data.error);
      }
    } catch (err) {
      console.error('Trend API failed', err);
      setTrendData([]);
      setTrendMessage('No data available');
    } finally {
      setLoadingTrend(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchExamTitles();
    fetchPieSummary();
    fetchDateRangeData();
    fetchTrendData();
  }, [departmentguid]);

  useEffect(() => {
    fetchDateRangeData();  // Always fetch when dates change, regardless of department
  }, [effectiveFromDate, effectiveToDate]);

  useEffect(() => {
    fetchTrendData();
  }, [selectedPeriod, departmentguid]);

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setSelectedTitle(title);
    setCurrentExamTitle(title || 'No exams completed');
    fetchPieSummary(title);
  };

  // Format date range display
  const formatDateRange = () => {
    if (!effectiveFromDate || !effectiveToDate) return 'Select date range';

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    return `${formatDate(effectiveFromDate)} - ${formatDate(effectiveToDate)}`;
  };

  // Custom bar shape with gradient
  const CustomBar = (props) => {
    const { x, y, width, height, index } = props;
    const color = dateBarColors[index % dateBarColors.length];

    return (
      <g>
        <defs>
          <linearGradient id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.8} />
            <stop offset="100%" stopColor={color} stopOpacity={0.3} />
          </linearGradient>
        </defs>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={`url(#gradient-${index})`}
          rx={4}
          stroke={color}
          strokeWidth={1}
        />
      </g>
    );
  };

  // Custom trend bar shape
  const CustomTrendBar = (props) => {
    const { x, y, width, height, index } = props;
    const color = trendBarColors[index % trendBarColors.length];

    return (
      <g>
        <defs>
          <linearGradient id={`trend-gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.9} />
            <stop offset="100%" stopColor={color} stopOpacity={0.4} />
          </linearGradient>
        </defs>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={`url(#trend-gradient-${index})`}
          rx={4}
          stroke={color}
          strokeWidth={1}
        />
      </g>
    );
  };

  // Render single loading overlay
  if (isLoading) {
    return (
      <div className="analytics-grid-vertical mt-4" style={{ position: 'relative', minHeight: '1000px' }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <img
            src="/images/breath1.gif"
            alt="Loading analytics..."
            style={{
              width: '500px',
              height: '500px',
              objectFit: 'contain'
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-grid-vertical mt-4">
      {/* Big Card - Exam Performance by Date */}
      <div className="analytics-card big-card">
        <div className="analytics-card-header">
          <div className="analytics-card-title">
            <Calendar className="analytics-card-icon" />
            <h3>Assessment Performance (Avg Score)</h3>
            <button
              className="filters-toggle-btn"
              onClick={() => setShowDateFilters(!showDateFilters)}
              title="Toggle chart type"
            >
              {/* <ChevronDown size={16} className={showDateFilters ? 'rotated' : ''} /> */}
            </button>
          </div>
        </div>

        {/* Chart Type Selector */}
        {/* {showDateFilters && (
          <div className="analytics-filters">
            <div className="filter-group full-width">
              <label>Chart Type</label>
              <select
                value={dateChartType}
                onChange={(e) => setDateChartType(e.target.value)}
                className="chart-type-select"
              >
                <option value="bar">Multi-Color Bar Chart</option>
                <option value="line">Rainbow Line Chart</option>
                <option value="pie">Vibrant Pie Chart</option>
                <option value="radar">Colorful Radar Chart</option>
              </select>
            </div>
          </div>
        )} */}

        {/* Chart */}
        {loadingDate ? (
          <div className="chart-empty" style={{ height: '350px' }}>
            <p>Loading...</p>
          </div>
        ) : dateRangeData.length === 0 ? (
          <div className="chart-empty" style={{ height: '350px' }}>
            <p>{emptyMessage}</p>
            {/* <button className="retry-btn" onClick={fetchDateRangeData}>
              Retry
            </button> */}
          </div>
        ) : (
          <div className="chart-container" style={{ height: '350px' }}>
            <ResponsiveContainer width="100%" height="105%">
              {dateChartType === 'bar' && (
                <BarChart
                  data={dateRangeData}
                  barSize={40}
                  margin={{ top: 10, right: 30, left: 30, bottom: 80 }} // Increased bottom margin
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="name"
                    height={120}
                    tick={({ x, y, payload }) => {
                      const label = payload.value;
                      return (
                        <g transform={`translate(${x - 5},${y + 15})`}>
                          <text
                            x={0}
                            y={0}
                            dy={0}
                            textAnchor="end"
                            fill="#666"
                            fontSize={11}
                            transform="rotate(-45)"
                          >
                            {label}
                          </text>
                        </g>
                      );
                    }}
                    interval={0}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    label={{
                      value: 'Average Score %',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 5,
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <Tooltip
                    formatter={(value) => [`${value}%`, 'Average Score']}
                    labelFormatter={(label) => {
                      const fullData = dateRangeData.find(d => d.name === label);
                      return fullData?.fullExamName || label;
                    }}
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', border: 'none' }}
                  />
                  <Bar
                    dataKey="avgScore"
                    name="Average Score"
                    shape={<CustomBar />}
                  />
                </BarChart>
              )}

              {dateChartType === 'line' && (
                <LineChart data={dateRangeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    label={{
                      value: 'Average Score %',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 10
                    }}
                  />
                  <Tooltip
                    formatter={(value) => [`${value}%`, 'Average Score']}
                    labelFormatter={(label) => {
                      const fullData = dateRangeData.find(d => d.name === label);
                      return fullData?.fullExamName || label;
                    }}
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="avgScore"
                    stroke="url(#lineGradient)"
                    strokeWidth={3}
                    dot={{
                      r: 5,
                      stroke: 'white',
                      strokeWidth: 2,
                      fill: ({ index }) => rainbowColors[index % rainbowColors.length]
                    }}
                    activeDot={{ r: 8 }}
                    name="Average Score"
                  />
                  <defs>
                    <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                      {rainbowColors.map((color, index) => (
                        <stop
                          key={index}
                          offset={`${(index * 100) / (rainbowColors.length - 1)}%`}
                          stopColor={color}
                        />
                      ))}
                    </linearGradient>
                  </defs>
                </LineChart>
              )}

              {dateChartType === 'pie' && (
                <PieChart>
                  <Pie
                    data={dateRangeData}
                    dataKey="avgScore"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    innerRadius={40}
                    label={({ name, avgScore }) => `${name}: ${avgScore}%`}
                    labelLine={true}
                  >
                    {dateRangeData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={dateBarColors[index % dateBarColors.length]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value}%`, 'Average Score']}
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px' }}
                  />
                  <Legend
                    wrapperStyle={{
                      paddingTop: '20px',
                      fontSize: '12px'
                    }}
                  />
                </PieChart>
              )}

              {dateChartType === 'radar' && (
                <RadarChart data={dateRangeData} outerRadius={100}>
                  <PolarGrid stroke="#e0e0e0" />
                  <PolarAngleAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <defs>
                    <linearGradient id="radarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF6B6B" stopOpacity={0.7} />
                      <stop offset="50%" stopColor="#4ECDC4" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#96CEB4" stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <Radar
                    name="Avg Score"
                    dataKey="avgScore"
                    stroke="url(#radarGradient)"
                    fill="url(#radarGradient)"
                    fillOpacity={0.6}
                  />
                </RadarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Two Smaller Cards Below */}
      <div className="small-cards-container">
        {/* Performance Trend Card */}
        <div className="analytics-card small-card">
          <div className="analytics-card-header">
            <div className="analytics-card-title">
              <TrendingUp className="analytics-card-icon" />
              <h3>Performance Trend</h3>
              <button
                className="filters-toggle-btn"
                onClick={() => setShowTrendFilters(!showTrendFilters)}
                title="Toggle filters"
              >
                <ChevronDown size={16} className={showTrendFilters ? 'rotated' : ''} />
              </button>
            </div>
          </div>

          {/* Collapsible Filters - Only Time Period & Chart Type */}
          {showTrendFilters && (
            <div className="analytics-filters">
              <div className="filter-group">
                <label>Time Period</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="chart-type-select"
                >
                  <option value="yearly">Yearly</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="weekly">This Week</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Chart Type</label>
                <select
                  value={trendChartType}
                  onChange={(e) => setTrendChartType(e.target.value)}
                  className="chart-type-select"
                >
                  <option value="bar">Multi-Color Bar</option>
                  <option value="line">Rainbow Line</option>
                  <option value="pie">Vibrant Pie</option>
                  <option value="radar">Colorful Radar</option>
                </select>
              </div>
            </div>
          )}

          {/* Trend Chart */}
          {loadingTrend ? (
            <div className="chart-empty" style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p>Loading...</p>
            </div>
          ) : trendMessage ? (
            <div className="chart-empty" style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ fontSize: '16px', color: '#666' }}>No data available</p>
            </div>
          ) : (
            <div className="chart-container" style={{ height: '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {trendChartType === 'bar' && (
                  <BarChart
                    data={trendData}
                    barSize={25}
                    margin={{ top: 10, right: 20, left: 30, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      tick={{ fontSize: 10 }}
                      interval={0}
                      tickMargin={10}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      label={{ value: 'Assessments', angle: -90,
                      position: 'insideLeft',
                      offset: 5,
                      style: { textAnchor: 'middle' } }}
                    />
                    <Tooltip formatter={v => [`${v}`, 'Assessments']} />
                    <Bar dataKey="examCount" name="Exams Created" shape={<CustomTrendBar />} />
                  </BarChart>
                )}
                {trendChartType === 'line' && (
                  <LineChart data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 10 }}
                      interval={0}  // Forces ALL labels to appear
                      tickMargin={10}
                      // ticks={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={v => [`${v}`, 'Assessments']}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px' }}
                    />
                    <defs>
                      <linearGradient id="trendLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FF6B6B" />
                        <stop offset="25%" stopColor="#F7DC6F" />
                        <stop offset="50%" stopColor="#82E0AA" />
                        <stop offset="75%" stopColor="#85C1E9" />
                        <stop offset="100%" stopColor="#BB8FCE" />
                      </linearGradient>
                    </defs>
                    <Line
                      type="monotone"
                      dataKey="examCount"
                      stroke="url(#trendLineGradient)"
                      strokeWidth={2}
                      dot={{
                        r: 3,
                        stroke: 'white',
                        strokeWidth: 1,
                        fill: ({ index }) => rainbowColors[index % rainbowColors.length]
                      }}
                      activeDot={{ r: 5 }}
                      name="Assessments Created"
                    />
                  </LineChart>
                )}

                {trendChartType === 'pie' && (
                  (() => {
                    // Filter out zero values for pie chart
                    const pieChartData = trendData.filter(item => item.examCount > 0);

                    if (pieChartData.length === 0) {
                      return (
                        <div className="chart-empty" style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <p style={{ fontSize: '14px', color: '#999' }}>No data available for pie chart</p>
                        </div>
                      );
                    }

                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            dataKey="examCount"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            innerRadius={20}
                            label={({ name, examCount }) => `${name}: ${examCount}`}
                            labelLine={true}
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell
                                key={index}
                                fill={trendPieColors[index % trendPieColors.length]}
                                stroke="#fff"
                                strokeWidth={1}
                              />
                            ))}
                          </Pie>
                          <Tooltip formatter={v => [`${v}`, 'Assessments']} />
                          <Legend wrapperStyle={{ fontSize: '10px', maxHeight: '80px', overflowY: 'auto' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    );
                  })()
                )}

                {trendChartType === 'radar' && (
                  <RadarChart data={trendData} outerRadius={65} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <PolarGrid stroke="#e0e0e0" />
                    <PolarAngleAxis dataKey="name" tick={{ fontSize: 9 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fontSize: 8 }} />
                    <Tooltip
                      formatter={(value) => [`${value} Assessment(s)`, 'Count']}
                      labelFormatter={(label) => `Month: ${label}`}
                    />
                    <Radar
                      name="Assessments Created"
                      dataKey="examCount"
                      stroke="#FF6B6B"
                      fill="#FF6B6B"
                      fillOpacity={0.6}
                      strokeWidth={2}
                    />
                  </RadarChart>
                )}
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Result Summary Doughnut Card */}
        <div className="analytics-card small-card">
          <div className="analytics-card-header">
            <div className="analytics-card-title">
              <PieIcon className="analytics-card-icon" />
              <h3 className="exam-title-truncate">Result Summary</h3>
              <button
                className="filters-toggle-btn"
                onClick={() => setShowExamSelect(!showExamSelect)}
                title="Select Assessment"
              >
                <ChevronDown size={16} className={showExamSelect ? 'rotated' : ''} />
              </button>
            </div>
          </div>

          {/* Collapsible Exam Select & Chart Type */}
          {showExamSelect && (
            <div className="analytics-filters">
              <div className="filter-group">
                <label>Select Assessment</label>
                <select
                  value={selectedTitle}
                  onChange={handleTitleChange}
                  disabled={loadingTitles || !!titlesError || examTitles.length === 0}
                  className="exam-select"
                >
                  {loadingTitles ? <option>Loading...</option> :
                    titlesError ? <option>Error loading</option> :
                      examTitles.length === 0 ? <option>No assessments</option> :
                        examTitles.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {/* {titlesError && (
                  <button className="retry-btn" onClick={fetchExamTitles}>
                    Retry
                  </button>
                )} */}
              </div>
            </div>
          )}

          {loadingPie ? (
            <div className="chart-empty" style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p>Loading...</p>
            </div>
          ) : pieMessage ? (
            <div className="chart-empty" style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <p style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                No attempts recorded yet
              </p>
              {/* <p style={{ fontSize: '14px', color: '#999' }}>
                No data found
              </p> */}
            </div>
          ) : (
            <div className="chart-container" style={{ height: '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={55}
                    paddingAngle={2}
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={pieChartColors[index % pieChartColors.length]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={v => `${v}%`}
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px' }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: '11px',
                      lineHeight: '15px',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      width: '100%'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div >
  );
}
